/* eslint-disable no-empty-pattern */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable react/jsx-no-target-blank */
import React, { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import AppLoader from "../../../components/loader";
import { useAccess } from "../../../hooks/useAccess";
import { LIST_FILES_FROM_S3 } from "../../../apolloClient/operation/querys";
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import { format } from "date-fns";

interface FileItem {
  fileName: string;
  Url: string;
  lastModified: string;
}

type Order = "asc" | "desc";

interface DashboardProps {}

export const FileManager: React.FC<DashboardProps> = ({}) => {
  const { loading: accessLoading } = useAccess();
  const { data, loading, error, refetch } = useQuery(LIST_FILES_FROM_S3, {
    fetchPolicy: "cache-and-network",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof FileItem>("lastModified");
  const [order, setOrder] = useState<Order>("desc");

  // Extract unique file types from the data based on keywords in filename
  const fileTypes = useMemo(() => {
    if (!data?.listFilesFromS3) return [];

    // Define common document type keywords
    const typeKeywords = [
      'estimate',
      'affidavit',
      'invoice',
      'contract',
      'agreement',
      'receipt',
      'proposal',
      'quote',
      'statement',
      'report',
      'certificate',
      'permit',
      'license',
      'authorization',
      'waiver',
      'release',
      'disclosure',
      'notice',
      'letter',
      'form',
      'application',
      'claim',
      'bill',
      'payment',
      'refund',
      'credit',
      'order',
      'purchase',
      'inspection',
      'warranty',
      'guarantee'
    ];

    const types = new Set<string>();
    
    data.listFilesFromS3.forEach((file: FileItem) => {
      const fileName = file.fileName.toLowerCase();
      const nameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
      
      // Special handling for affidavit - keep signed and unsigned separate
      if (nameWithoutExtension.includes('affidavit')) {
        if (nameWithoutExtension.includes('signed')) {
          types.add('affidavit_signed');
        } else {
          types.add('affidavit');
        }
      }
      
      // Check for each keyword in the filename (excluding affidavit as it's handled above)
      typeKeywords.forEach(keyword => {
        if (keyword !== 'affidavit' && nameWithoutExtension.includes(keyword)) {
          types.add(keyword);
        }
      });
      
      // Also check for "signed" variants of common types (excluding affidavit)
      if (nameWithoutExtension.includes('signed') && !nameWithoutExtension.includes('affidavit')) {
        // Extract what type is signed (e.g., "estimate_signed" -> "estimate_signed")
        typeKeywords.forEach(keyword => {
          if (keyword !== 'affidavit' && (nameWithoutExtension.includes(`${keyword}_signed`) || nameWithoutExtension.includes(`signed_${keyword}`))) {
            types.add(`${keyword}_signed`);
          }
        });
      }
    });

    return Array.from(types).sort();
  }, [data]);

  // Filter and sort files
  const filteredAndSortedFiles = useMemo(() => {
    if (!data?.listFilesFromS3) return [];

    let files = [...data.listFilesFromS3];

    // Apply search filter
    if (searchQuery) {
      files = files.filter((file: FileItem) =>
        file.fileName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply file type filter
    if (fileTypeFilter !== "all") {
      files = files.filter((file: FileItem) => {
        const fileName = file.fileName.toLowerCase();
        return fileName.includes(fileTypeFilter.toLowerCase());
      });
    }

    // Apply sorting
    files.sort((a: FileItem, b: FileItem) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (orderBy === "lastModified") {
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        return order === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    return files;
  }, [data, searchQuery, fileTypeFilter, orderBy, order]);

  const handleRequestSort = (property: keyof FileItem) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenFile = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const getFileExtension = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toUpperCase();
    return ext || "FILE";
  };

  const getFileTypeChipColor = (fileName: string): "primary" | "secondary" | "success" | "error" | "warning" | "info" | "default" => {
    const lowerName = fileName.toLowerCase();
    // Check affidavit first (signed affidavit gets a different shade but same color family)
    if (lowerName.includes("affidavit")) {
      if (lowerName.includes("signed")) return "secondary"; // Dark purple for signed affidavit
      return "secondary"; // Same color for affidavit
    }
    if (lowerName.includes("estimate")) return "primary";
    if (lowerName.includes("order")) return "success"; // Green for order
    if (lowerName.includes("contract")) return "info";
    if (lowerName.includes("invoice") || lowerName.includes("bill")) return "warning";
    return "default";
  };

  if (accessLoading || loading) {
    return <AppLoader isLoading={true} />;
  }

  if (error) {
    return (
      <Box sx={{ width: "100%", height: "100vh", overflow: "auto", bgcolor: "#f6f8fa" }}>
        <Box sx={{ p: 3 }}>
          <Typography color="error">Error loading files: {error.message}</Typography>
        </Box>
      </Box>
    );
  }

  const paginatedFiles = filteredAndSortedFiles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto", bgcolor: "#f6f8fa" }}>
      <Box sx={{ p: 3, maxWidth: "100%" }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#22223b", mb: 1 }}>
            File Manager
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage and view all files from S3 storage
          </Typography>
        </Box>

        {/* Color Legend - Dynamic based on actual file types */}
        {fileTypes.length > 0 && (
          <Box sx={{ mb: 2, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#64748b" }}>
              File Type Colors:
            </Typography>
            {fileTypes.includes('estimate') && (
              <Chip label="Estimate" size="small" color="primary" sx={{ fontWeight: 600 }} />
            )}
            {fileTypes.includes('estimate_signed') && (
              <Chip label="Estimate Signed" size="small" color="primary" sx={{ fontWeight: 600 }} />
            )}
            {fileTypes.includes('affidavit') && (
              <Chip label="Affidavit" size="small" color="secondary" sx={{ fontWeight: 600, bgcolor: "#9c27b0", color: "white" }} />
            )}
            {fileTypes.includes('affidavit_signed') && (
              <Chip label="Signed Affidavit" size="small" color="secondary" sx={{ fontWeight: 600, bgcolor: "#7b1fa2", color: "white" }} />
            )}
            {fileTypes.includes('order') && (
              <Chip label="Order" size="small" color="success" sx={{ fontWeight: 600 }} />
            )}
            {fileTypes.includes('contract') && (
              <Chip label="Contract" size="small" color="info" sx={{ fontWeight: 600 }} />
            )}
            {fileTypes.includes('invoice') && (
              <Chip label="Invoice" size="small" color="warning" sx={{ fontWeight: 600 }} />
            )}
          </Box>
        )}

        {/* Filters Card */}
        <Card
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            boxShadow: "0 2px 8px 0 rgba(31, 38, 135, 0.08)",
          }}
        >
          <Box sx={{ 
            display: "flex", 
            gap: 2, 
            flexWrap: "wrap", 
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
              <Box sx={{ flex: "1 1 300px", minWidth: 250, maxWidth: 500, position: 'relative' }}>
                <SearchIcon sx={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '20px'
                }} />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 40px',
                    fontSize: '14px',
                    border: '1px solid #d0d5dd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
              </Box>
              <Box sx={{ flex: "0 1 220px", minWidth: 200 }}>
                <select
                  value={fileTypeFilter}
                  onChange={(e) => {
                    setFileTypeFilter(e.target.value);
                    setPage(0);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #d0d5dd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="all">All Files</option>
                  {fileTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, " ").toUpperCase()}
                    </option>
                  ))}
                </select>
              </Box>
            </Box>
            <Chip 
              label={`${filteredAndSortedFiles.length} ${filteredAndSortedFiles.length === 1 ? 'file' : 'files'}`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: "#e0e7ff", color: "#3730a3", flexShrink: 0 }}
            />
          </Box>
        </Card>

        {/* Table Card */}
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 12px 0 rgba(31, 38, 135, 0.08)",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, width: "50%" }}>
                    <TableSortLabel
                      active={orderBy === "fileName"}
                      direction={orderBy === "fileName" ? order : "asc"}
                      onClick={() => handleRequestSort("fileName")}
                    >
                      File Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={orderBy === "lastModified"}
                      direction={orderBy === "lastModified" ? order : "asc"}
                      onClick={() => handleRequestSort("lastModified")}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <DescriptionIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
                      <Typography color="textSecondary">
                        {searchQuery || fileTypeFilter !== "all"
                          ? "No files found matching your filters"
                          : "No files available"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedFiles.map((file: FileItem, index: number) => (
                    <TableRow
                      key={`${file.fileName}-${index}`}
                      hover
                      sx={{
                        "&:hover": { bgcolor: "#f8fafc" },
                        cursor: "pointer",
                      }}
                      onClick={() => handleOpenFile(file.Url)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DescriptionIcon sx={{ color: "#64748b" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              // wordBreak: "break-word",
                            }}
                          >
                            {file.fileName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getFileExtension(file.fileName)}
                          size="small"
                          color={getFileTypeChipColor(file.fileName)}
                          sx={{ 
                            fontWeight: 600,
                            ...(file.fileName.toLowerCase().includes("affidavit") && file.fileName.toLowerCase().includes("signed") 
                              ? { bgcolor: "#7b1fa2", color: "white" } // Dark purple for signed affidavit
                              : file.fileName.toLowerCase().includes("affidavit")
                              ? { bgcolor: "#9c27b0", color: "white" } // Light purple for affidavit
                              : {})
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(file.lastModified)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenFile(file.Url);
                          }}
                          sx={{
                            "&:hover": {
                              bgcolor: "primary.light",
                              color: "white",
                            },
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredAndSortedFiles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Box>
    </Box>
  );
};
